import {
  ArrowRight,
  CircleCheck,
  Globe2,
  LockKeyhole,
  MonitorUp,
  Radio,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/Badge";
import { buttonStyles } from "../components/Button";
import { LogoLink } from "../components/Logo";
import { Avatar } from "../components/Avatar";

const features = [
  {
    icon: Zap,
    title: "P2P без задержек",
    text: "Видео идёт напрямую между браузерами. Сервер только договаривается о соединении и не трогает поток.",
  },
  {
    icon: MonitorUp,
    title: "До 1440p при 60 FPS",
    text: "Выбирайте весь экран, окно или вкладку. Разрешение и частота кадров проверяются по фактическим параметрам трека.",
  },
  {
    icon: Users,
    title: "Комната на троих",
    text: "Один ведущий и два зрителя. Подключайтесь по ссылке, без установки приложений и плагинов.",
  },
  {
    icon: Globe2,
    title: "Работает через интернет",
    text: "STUN и TURN с временными credentials помогают установить соединение даже за строгим NAT.",
  },
  {
    icon: LockKeyhole,
    title: "Закрытый доступ",
    text: "Аккаунты, серверные сессии, Argon2id и HTTPS/WSS. Смотреть комнату могут только авторизованные пользователи.",
  },
  {
    icon: ActivityIcon,
    title: "Честная статистика",
    text: "RTT, потери пакетов, битрейт и FPS берутся из RTCPeerConnection.getStats(), а не рисуются для красоты.",
  },
];

const steps = [
  {
    number: "01",
    title: "Создайте комнату",
    text: "Назовите трансляцию и откройте страницу ведущего. Ссылку можно сразу отправить друзьям.",
  },
  {
    number: "02",
    title: "Выберите экран",
    text: "Системный picker браузера покажет доступные мониторы, окна и вкладки. Выберите источник и проверьте preview.",
  },
  {
    number: "03",
    title: "Подключите зрителей",
    text: "Зрители находят активную комнату в кабинете и смотрят поток в браузере. Никаких установок.",
  },
];

function ActivityIcon(props: { className?: string }) {
  return <Radio {...props} />;
}

export function HomePage() {
  const { user } = useAuth();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-64 left-[8%] size-[620px] rounded-full bg-violet-600/[0.13] blur-[150px]" />
        <div className="absolute -top-40 right-[-10%] size-[520px] rounded-full bg-cyan-500/[0.08] blur-[140px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)]" />
      </div>

      <header className="relative z-10 mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6">
        <LogoLink />
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" className={buttonStyles("ghost", "md")}>
                Кабинет
              </Link>
              <Link to="/dashboard" className={buttonStyles("primary", "md")}>
                Открыть StreamRoom
                <ArrowRight className="size-4" />
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className={buttonStyles("ghost", "md")}>
                Войти
              </Link>
              <Link to="/register" className={buttonStyles("primary", "md")}>
                Создать аккаунт
                <ArrowRight className="size-4" />
              </Link>
            </>
          )}
        </div>
      </header>

      <main className="relative z-10">
        <section className="mx-auto grid max-w-7xl gap-16 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:pt-24">
          <div className="rise-in">
            <Badge tone="violet" className="mb-6">
              <Sparkles className="size-3" />
              Self-hosted · WebRTC · без установки
            </Badge>
            <h1 className="balance max-w-2xl text-[44px] font-semibold leading-[1.02] tracking-[-0.055em] text-white sm:text-6xl lg:text-[72px]">
              Покажите экран.
              <br />
              <span className="bg-gradient-to-r from-violet-300 via-white to-cyan-200 bg-clip-text text-transparent">
                Остальное — P2P.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-balance text-[16px] leading-relaxed text-zinc-400 sm:text-lg">
              StreamRoom — собственный сервис демонстрации экрана для небольшой
              компании. Браузерный picker, выбор разрешения и FPS, реальная
              статистика соединения.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={user ? "/dashboard" : "/register"}
                className={buttonStyles("primary", "lg")}
              >
                {user ? "Перейти в кабинет" : "Начать бесплатно"}
                <ArrowRight className="size-4" />
              </Link>
              <Link to="/login" className={buttonStyles("secondary", "lg")}>
                Уже есть аккаунт
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-zinc-500">
              <span className="inline-flex items-center gap-2">
                <CircleCheck className="size-3.5 text-emerald-400/80" />
                Видео не проходит через сервер
              </span>
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="size-3.5 text-cyan-400/80" />
                HTTPS / WSS
              </span>
              <span className="inline-flex items-center gap-2">
                <Users className="size-3.5 text-violet-400/80" />1 ведущий + 2
                зрителя
              </span>
            </div>
          </div>

          <div className="rise-in relative mx-auto w-full max-w-[620px] [animation-delay:120ms]">
            <div className="absolute -inset-8 rounded-[40px] bg-[radial-gradient(closest-side,rgba(139,92,246,0.16),transparent)] blur-2xl" />
            <div className="noise relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d13]/90 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl">
              <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="size-2.5 rounded-full bg-rose-400/70" />
                  <span className="size-2.5 rounded-full bg-amber-300/70" />
                  <span className="size-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone="rose">LIVE</Badge>
                  <span className="font-mono text-[10px] text-zinc-600">
                    1440p · 60
                  </span>
                </div>
              </div>

              <div className="relative aspect-[16/10] overflow-hidden bg-[#090b10] p-5">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(139,92,246,0.18),transparent_45%),radial-gradient(circle_at_90%_100%,rgba(34,211,238,0.12),transparent_45%)]" />
                <div className="relative flex h-full flex-col rounded-lg border border-white/[0.06] bg-[#0d1017]/85 p-4">
                  <div className="flex items-center gap-2 border-b border-white/[0.05] pb-3">
                    <div className="size-2 rounded-full bg-violet-400/80" />
                    <div className="h-1.5 w-28 rounded-full bg-white/10" />
                    <div className="ml-auto h-1.5 w-14 rounded-full bg-white/[0.06]" />
                  </div>
                  <div className="mt-4 grid flex-1 grid-cols-[1.25fr_0.75fr] gap-4">
                    <div className="space-y-2.5 rounded-lg border border-white/[0.05] bg-black/20 p-4">
                      <div className="h-2 w-1/3 rounded-full bg-violet-400/30" />
                      <div className="h-2 w-4/5 rounded-full bg-white/[0.07]" />
                      <div className="h-2 w-3/5 rounded-full bg-white/[0.06]" />
                      <div className="mt-5 space-y-2 rounded-md border border-white/[0.05] bg-[#080a0f] p-3">
                        <div className="h-1.5 w-2/5 rounded-full bg-cyan-400/25" />
                        <div className="h-1.5 w-3/5 rounded-full bg-white/[0.06]" />
                        <div className="h-1.5 w-1/2 rounded-full bg-white/[0.05]" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
                        <div className="flex items-center gap-2">
                          <Avatar name="Аня" size="sm" />
                          <div className="space-y-1">
                            <div className="h-1.5 w-16 rounded-full bg-white/10" />
                            <div className="h-1.5 w-10 rounded-full bg-white/[0.05]" />
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
                        <div className="flex items-center gap-2">
                          <Avatar name="Максим" size="sm" />
                          <div className="space-y-1">
                            <div className="h-1.5 w-14 rounded-full bg-white/10" />
                            <div className="h-1.5 w-12 rounded-full bg-white/[0.05]" />
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg border border-emerald-400/15 bg-emerald-500/[0.05] p-3">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-emerald-300/70">
                          Direct P2P
                        </p>
                        <p className="mt-1 font-mono text-[10px] text-emerald-100/80">
                          12.4 Мбит/с
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-white/[0.07] px-4 py-3">
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <span className="size-1.5 rounded-full bg-emerald-400" />
                  Соединение стабильно
                </div>
                <span className="font-mono text-[10px] text-zinc-600">
                  0.0% loss · 24 ms
                </span>
              </div>
            </div>
            <div className="absolute -bottom-8 -left-6 hidden rounded-xl border border-white/10 bg-[#0d0f15]/90 px-4 py-3 shadow-xl backdrop-blur-xl sm:block">
              <p className="text-[10px] uppercase tracking-widest text-zinc-600">
                Источник
              </p>
              <p className="mt-1 text-[12px] font-medium text-zinc-200">
                Весь экран · 2560×1440
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.05] bg-white/[0.012]">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
            {features.map((feature) => (
              <div key={feature.title} className="group">
                <div className="mb-4 flex size-10 items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.03] transition-colors group-hover:border-violet-400/20 group-hover:bg-violet-500/[0.07]">
                  <feature.icon className="size-4.5 text-zinc-400 transition-colors group-hover:text-violet-300" />
                </div>
                <h3 className="text-[15px] font-medium text-zinc-100">
                  {feature.title}
                </h3>
                <p className="mt-2 max-w-sm text-[13px] leading-relaxed text-zinc-500">
                  {feature.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <div className="max-w-xl">
            <Badge tone="cyan">Как это работает</Badge>
            <h2 className="mt-5 text-balance text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl">
              Три шага до общего экрана
            </h2>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {steps.map((step) => (
              <div
                key={step.number}
                className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.018] p-6"
              >
                <span className="font-mono text-[11px] text-violet-300/60">
                  {step.number}
                </span>
                <h3 className="mt-8 text-[16px] font-medium text-zinc-100">
                  {step.title}
                </h3>
                <p className="mt-2 text-[13px] leading-relaxed text-zinc-500">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-gradient-to-br from-violet-500/[0.08] via-white/[0.02] to-cyan-500/[0.06] px-6 py-14 text-center sm:px-12">
            <div className="pointer-events-none absolute -top-32 left-1/2 size-80 -translate-x-1/2 rounded-full bg-violet-500/15 blur-[100px]" />
            <h2 className="relative text-balance text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl">
              Свой экран — в двух кликах
            </h2>
            <p className="relative mx-auto mt-3 max-w-xl text-[14px] leading-relaxed text-zinc-400">
              Создайте аккаунт, комнату и отправьте ссылку друзьям. Всё
              остальное браузер сделает сам.
            </p>
            <Link
              to={user ? "/dashboard" : "/register"}
              className={buttonStyles("primary", "lg", "relative mt-7")}
            >
              {user ? "Открыть кабинет" : "Создать аккаунт"}
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/[0.05]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-4 py-8 text-[12px] text-zinc-600 sm:flex-row sm:items-center sm:px-6">
          <div className="flex items-center gap-3">
            <LogoLink />
            <span className="text-zinc-700">·</span>
            <span>Self-hosted screen sharing</span>
          </div>
          <p>WebRTC P2P · 1 ведущий + 2 зрителя · без записи</p>
        </div>
      </footer>
    </div>
  );
}
